import { Flex, useBreakpointValue } from "@chakra-ui/react";
import { uniq } from "lodash";
import { useMemo } from "react";

import useCartHooks from "components/hooks/Cart/useCartHooks";
import { OrderModel, OrderStatus } from "graphql/generated";
import getNameProp from "src/utils/getNameProp";

import { AddButton, BuyButton } from "../../Shop/Buttons";

interface Props {
  t: (key: string) => string;
  requiresPrescription: boolean;
  productProps: any;
  hideBuyButton?: boolean;
  hideAddbutton?: boolean;
  fromThankYou?: boolean;
  isSuggestion?: boolean;
  buttonDirection?: "column" | "row";
  onClickBuyButton?: (product: any) => void;
  teleConsultOrders?: OrderModel[];
  excludedEvalIds?: string[];
}

const ButtonSection = ({
  t,
  requiresPrescription,
  productProps,
  hideBuyButton = false,
  hideAddbutton,
  fromThankYou = false,
  isSuggestion = false,
  buttonDirection,
  onClickBuyButton,
  teleConsultOrders = [],
  excludedEvalIds
}: Props) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const {
    onAddToCartClick,
    onBuyNowClick,
    onThankYouAddToCartClick,
    onThankYouBuyNowClick,
    completedEvalIds,
    skippedEvalIds,
    platform
  } = useCartHooks(isSuggestion, excludedEvalIds);
  // after doctor approve meds (skip meds selection flow)
  const completedConsultEvalIDs = teleConsultOrders
    .filter((o) => o.status === OrderStatus.Deleted)
    .map((o) => o.evaluationId);

  let showAddButton =
    !requiresPrescription ||
    uniq([...completedEvalIds, ...skippedEvalIds, ...completedConsultEvalIDs]).includes(
      productProps?.evaluationId?.[platform]
    );

  if (hideAddbutton !== undefined) {
    showAddButton = !hideAddbutton;
  }

  const clickBuyButton = () => {
    if (onClickBuyButton) {
      onClickBuyButton(productProps);
      return;
    }

    if (fromThankYou) {
      onThankYouBuyNowClick(productProps);
      return;
    }

    onBuyNowClick(productProps);
  };

  const direction = useMemo((): string | string[] => {
    if (buttonDirection) {
      return buttonDirection;
    }

    if (hideBuyButton) {
      return "column";
    }

    return ["column", "row"]; // responsive
  }, [buttonDirection, hideBuyButton]);

  return (
    <Flex
      alignItems={hideBuyButton ? "flex-start" : "center"}
      direction={direction as any[]}
      w={buttonDirection === "column" ? "100%" : showAddButton ? "100%" : "50%"}
      gap="12px"
      sx={
        buttonDirection !== "column" && {
          "& [role=cartCTA] + [role=cartCTA]": {
            ml: [0, "12px"],
            mt: ["12px", 0]
          }
        }
      }
    >
      {showAddButton && (
        <AddButton
          t={t}
          onClick={() =>
            fromThankYou ? onThankYouAddToCartClick(productProps) : onAddToCartClick(productProps)
          }
          product={productProps}
          hideBuyButton={hideBuyButton}
          requiresPrescription={requiresPrescription}
          {...((buttonDirection === "column" || isMobile) && { width: "100%" })}
          {...getNameProp("add-button-section")}
        />
      )}

      {!hideBuyButton && (
        <BuyButton
          onClick={clickBuyButton}
          t={t}
          showAddButton={showAddButton}
          requiresPrescription={requiresPrescription}
          {...((buttonDirection === "column" || isMobile) && {
            width: "100% !important"
          })}
          {...getNameProp("buy-button-section")}
        />
      )}
    </Flex>
  );
};

export default ButtonSection;
